/**
 * **The walk-up** (F3): New person, the visitor (ADR 0032), and the walk-up waiting on its waiver.
 */
import { type ReactNode } from 'react';
import { VisitorEntry } from './BoothAdmin.js';
import { PlaceOn } from './BoothDesk.js';
import { first, hours } from './BoothShared.js';
import { useBooth } from './BoothState.js';

/** A walk-up made and waiting on the year’s waiver on the guest screen. */
export function PendingWalkUp(): ReactNode {
  const { setDone, setTag, busy, guest, pending, setPending, show, act, day, placeChoices } =
    useBooth();
  return (
    <>
      {pending ? (
        <section className="booth__card" data-testid="booth-pending">
          <div className="booth__cardhead">{pending.name.toUpperCase()} · ON THE GUEST SCREEN</div>
          <div className="booth__row">
            <span className="booth__k">WAIVER</span>
            <span>
              {guest?.state === 'walkup'
                ? 'Finishing their details on the guest screen.'
                : guest?.signed
                  ? 'Signed on the guest screen.'
                  : 'Waiting for the signature on the guest screen.'}
            </span>
          </div>
          <button
            type="button"
            className="booth__big"
            disabled={busy || !guest?.signed || guest.state === 'walkup'}
            onClick={() => {
              const sh = placeChoices.find((x) => x.shiftId === pending.shiftId);
              const who = pending;
              act(
                '/volunteers/booth/check-in',
                { shiftId: who.shiftId, personId: who.personId, walkUp: true },
                (r) => {
                  setPending(null);
                  setDone(`${who.name}: ${r.says}`);
                  if (sh)
                    setTag({
                      name: who.name,
                      area: sh.area,
                      hours: hours(sh.startsAt, sh.endsAt),
                      day,
                      code: r.tag,
                    });
                  show('tag', who.personId, who.shiftId);
                },
              );
            }}
          >
            Check in {first(pending.name)}
          </button>
          <button
            type="button"
            className="booth__link"
            onClick={() => {
              setPending(null);
              show('welcome');
            }}
          >
            Not now
          </button>
        </section>
      ) : null}
    </>
  );
}

/** New person: a name and a phone, placed on the shortest shift — or a visitor. */
export function WalkUp(): ReactNode {
  const {
    setQ,
    placeOn,
    setPlaceOn,
    walkUp,
    visitorOpen,
    setVisitorOpen,
    given,
    setGiven,
    family,
    setFamily,
    mobile,
    setMobile,
    samePhone,
    setDone,
    setTag,
    busy,
    guest,
    setPending,
    show,
    reset,
    act,
    day,
    placeChoices,
    printTag,
  } = useBooth();
  return (
    <>
      {/* ADR 0032: a visitor — not a volunteer — from the same walk-up. */}
      {walkUp ? (
        <VisitorEntry
          onOpen={setVisitorOpen}
          onTag={(name, hereFor) => {
            // The visitor tag (Booth.dc.html `visitor`): first name large, no area band,
            // VISITOR and who they are here for. No QR — a visitor checks out on Who's
            // here now.
            setTag({ name, area: `VISITOR · Here for: ${hereFor}`, hours: '', day });
            setTimeout(() => {
              printTag();
            }, 50);
          }}
        />
      ) : null}
      {walkUp && !visitorOpen ? (
        <form
          className="booth__found"
          data-testid="booth-walkup"
          onSubmit={(e) => {
            e.preventDefault();
            const s = placeChoices.find((x) => x.shiftId === placeOn);
            const handOff = !!guest?.on;
            act(
              '/volunteers/booth/walk-up',
              {
                givenName: given,
                familyName: family,
                mobile: mobile || null,
                shiftId: placeOn,
                ...(handOff ? { handOff: true } : {}),
              },
              (r) => {
                setDone(r.says);
                const made = r as { personId?: string; needsWaiver?: boolean };
                // Handed to the guest screen (state 10): they finish their own details and
                // sign; the desk checks them in from the waiting card.
                if (handOff && made.personId) {
                  setPending({
                    personId: made.personId,
                    name: r.name ?? `${given} ${family}`,
                    shiftId: placeOn,
                  });
                  show('walkup', made.personId, placeOn);
                  return;
                }
                if (made.needsWaiver && made.personId) {
                  setPending({
                    personId: made.personId,
                    name: r.name ?? `${given} ${family}`,
                    shiftId: placeOn,
                  });
                  show('waiver', made.personId, placeOn);
                  return;
                }
                if (s)
                  setTag({
                    name: r.name ?? `${given} ${family}`,
                    area: s.area,
                    hours: hours(s.startsAt, s.endsAt),
                    day,
                    code: r.tag,
                  });
              },
            );
          }}
        >
          <section className="booth__card">
            <div className="booth__cardhead">
              WHAT YOU TYPE
              <span className="booth__cardsub">the minimum</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>Name</b>
              </span>
              <label className="booth__rowc">
                <span className="booth__k">FIRST NAME</span>
                <input
                  className="booth__input"
                  required
                  value={given}
                  onChange={(e) => {
                    setGiven(e.target.value);
                  }}
                />
              </label>
              <label className="booth__rowc">
                <span className="booth__k">LAST NAME</span>
                <input
                  className="booth__input"
                  required
                  value={family}
                  onChange={(e) => {
                    setFamily(e.target.value);
                  }}
                />
              </label>
            </div>
            {samePhone ? (
              <div className="booth__row booth__row--warn" data-testid="booth-same-phone">
                <span className="booth__rowa">
                  <b>Did you mean?</b>
                  <span className="booth__rowb">
                    {samePhone.name} · {mobile}
                  </span>
                </span>
                <span className="booth__rowc">
                  Same phone. One tap makes this them, not a second person.
                </span>
                <span className="booth__chip booth__chip--warn">SAME PHONE</span>
                <button
                  type="button"
                  className="booth__act"
                  onClick={() => {
                    const name = samePhone.name;
                    reset();
                    setQ(name);
                  }}
                >
                  It’s them
                </button>
              </div>
            ) : null}
            <label className="booth__row">
              <span className="booth__rowa">
                <b className="booth__k">MOBILE</b>
              </span>
              <input
                className="booth__input"
                inputMode="tel"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value);
                }}
              />
              <span className="booth__rowc">
                So they can be found next year, and texted today if their shift moves.
              </span>
            </label>
            <PlaceOn shifts={placeChoices} value={placeOn} onChange={setPlaceOn} />
          </section>
          {guest?.on ? (
            <section className="booth__card booth__card--sky">
              <div className="booth__cardhead">
                WHAT THEY DO ON THE GUEST SCREEN
                <span className="booth__cardsub">while you help the next person</span>
              </div>
              <div className="booth__row">
                <span className="booth__rowa">
                  <b>Their email · their emergency contact</b>
                </span>
                <span className="booth__rowc">Large fields, finger or stylus.</span>
                <span className="booth__chip booth__chip--info">GUEST</span>
              </div>
              <div className="booth__row">
                <span className="booth__rowa">
                  <b>Reads and signs this year’s waiver</b>
                </span>
                <span className="booth__rowc" />
                <span className="booth__chip booth__chip--info">GUEST</span>
              </div>
              <div className="booth__cardfoot">
                They exist in People as “walk-up · the booth” the moment you press the button. No
                shift is made for them; they join the one you picked.
              </div>
            </section>
          ) : null}
          <div className="booth__bigrow">
            <button
              type="submit"
              className="booth__big"
              data-testid="booth-big"
              disabled={busy || !placeOn || !given.trim() || !family.trim()}
            >
              {guest?.on ? 'Hand to the guest screen' : `Check in ${given.trim() || 'them'}`}
              <span className="booth__bigsub">
                {guest?.on
                  ? 'they finish and sign; you check them in'
                  : 'the guest screen is off — they are checked in here'}
              </span>
            </button>
            <button type="button" className="booth__side-act" onClick={reset}>
              Back to search
            </button>
          </div>
        </form>
      ) : null}
    </>
  );
}
